/**
 * Idle detector behind `setupSessionRecordingOptimizations` (lib/analytics.ts):
 * calls `onIdle` after `idleMs` with no user activity, and `onActive` on the
 * first activity after that.
 *
 * Kept import-free so it is unit-testable without Mixpanel (lib/ specs share
 * one process, and several replace `@/lib/analytics` with a mock).
 *
 * Re-arming is throttled: `mousemove`/`scroll` fire dozens of times a second,
 * and each re-arm was a clearTimeout + setTimeout pair on the main thread.
 * Within `rearmThrottleMs` of the last re-arm an activity event is a no-op —
 * the idle deadline can land up to that much late, which is immaterial against
 * a 2-minute timeout. Resuming from idle is never throttled.
 */
export interface RecordingIdleDetectorOptions {
  idleMs: number;
  rearmThrottleMs: number;
  onIdle: () => void;
  onActive: () => void;
  now?: () => number;
}

export interface RecordingIdleDetector {
  /** Call on every user-activity event (and once at setup to arm). */
  onActivity: () => void;
}

export function createRecordingIdleDetector({
  idleMs,
  rearmThrottleMs,
  onIdle,
  onActive,
  now = Date.now,
}: RecordingIdleDetectorOptions): RecordingIdleDetector {
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  let isIdle = false;
  let lastArmedAt = Number.NEGATIVE_INFINITY;

  const onActivity = (): void => {
    const t = now();
    if (!isIdle && idleTimer !== null && t - lastArmedAt < rearmThrottleMs) {
      return;
    }

    if (idleTimer !== null) clearTimeout(idleTimer);

    if (isIdle) {
      onActive();
      isIdle = false;
    }

    lastArmedAt = t;
    idleTimer = setTimeout(() => {
      idleTimer = null;
      isIdle = true;
      onIdle();
    }, idleMs);
  };

  return { onActivity };
}
